import { useState, useEffect, useCallback } from 'react';

import { reportMemberSessionExpired } from '../services/memberApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Hook to fetch and check portal section visibility for a student.
 *
 * Default is hide-until-resolved: section-controlled tabs return false
 * until the API responds. Showing them by default leaks content to
 * 0-student accounts and flashes restricted tabs before resolution
 * (OWZ-11).
 *
 * @param {string} studentId
 * @param {string} token - auth token
 * @returns {{ isSectionVisible: (sectionId: string) => boolean, visibleSections: string[], isLoaded: boolean }}
 */
const usePortalSections = (studentId, token) => {
  const [visibleSections, setVisibleSections] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!studentId || !token) {
      setVisibleSections(null);
      setIsLoaded(true);
      return;
    }

    let cancelled = false;

    const fetchSections = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/customer/students/${studentId}/portal-sections`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!cancelled) {
          if (response.ok) {
            const data = await response.json();
            setVisibleSections(data.visible_sections);
          } else if (response.status === 401) {
            reportMemberSessionExpired();
          }
        }
      } catch {
        // Leave visibleSections null — default-deny applies
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    };

    setIsLoaded(false);
    fetchSections();

    return () => { cancelled = true; };
  }, [studentId, token]);

  const isSectionVisible = useCallback(
    (sectionId) => {
      if (!visibleSections) return false;
      return visibleSections.includes(sectionId);
    },
    [visibleSections]
  );

  return { isSectionVisible, visibleSections, isLoaded };
};

export default usePortalSections;
